(function () {
  'use strict';

  const OPT_OUT_KEY = 'theme:package-protection-opt-out';
  const LEGACY_CART_ATTRS = [
    '_pp_protection_variant_id',
    '_pp_protection_product_id',
    '_pp_pricing_mode',
    '_pp_percent',
    '_pp_min_cents',
    '_pp_offer_max_cart_cents',
  ];

  function getConfig() {
    return window.themeVariables?.packageProtection;
  }

  function getStorage() {
    const cfg = getConfig();
    if (!cfg?.enabled) return sessionStorage;
    return cfg.optOutStorage === 'local' ? localStorage : sessionStorage;
  }

  function getOptOut() {
    try {
      return getStorage().getItem(OPT_OUT_KEY) === '1';
    } catch {
      return false;
    }
  }

  function setOptOut(value) {
    try {
      if (value) {
        getStorage().setItem(OPT_OUT_KEY, '1');
      } else {
        getStorage().removeItem(OPT_OUT_KEY);
      }
    } catch {
      /* ignore */
    }
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    const p = precision == null ? 2 : precision;
    const t = thousands == null ? ',' : thousands;
    const d = decimal == null ? '.' : decimal;
    if (number == null || isNaN(number)) return '0';
    const fixed = (number / 100).toFixed(p);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
    const cents = parts[1] ? d + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    const useCurrency = window.themeVariables?.settings?.currencyCodeEnabled;
    const formatString = useCurrency
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
    if (!formatString) return String(cents / 100);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return formatWithDelimiters(cents, 2);
    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function applyLabelTemplate(template, tokens) {
    let out = template || '';
    for (const [key, value] of Object.entries(tokens)) {
      out = out.split(`{${key}}`).join(value);
    }
    return out;
  }

  function checkoutButtonLabel(cfg, cartTotalCents) {
    const template = cfg.checkoutButtonText?.trim();
    if (template) {
      return applyLabelTemplate(template, { total: formatMoney(cartTotalCents) });
    }
    const prefix = cfg.checkoutLabelPrefix || 'CHECKOUT+';
    return `${prefix} • ${formatMoney(cartTotalCents)}`;
  }

  function widgetRowLabel(cfg, templateFromDom, feeCents) {
    const template =
      templateFromDom?.trim() ||
      cfg.widgetRowText?.trim() ||
      'Checkout+ Package Protection • {fee}';
    return applyLabelTemplate(template, { fee: formatMoney(feeCents) });
  }

  function getBundledSectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  function merchandiseSubtotalCents(cart, productId) {
    if (!cart?.items) return 0;
    const pid = Number(productId);
    return cart.items.reduce((sum, item) => {
      if (Number(item.product_id) === pid) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function protectionLineItems(cart, productId) {
    if (!cart?.items) return [];
    const pid = Number(productId);
    return cart.items.filter((item) => Number(item.product_id) === pid);
  }

  function offerMaxCartCents(cfg) {
    const raw = cfg.offerMaxCartCents ?? cfg.maxCents;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return !isNaN(n) && n > 0 ? n : null;
  }

  function isOfferEligible(merchCents, cfg) {
    if (merchCents <= 0) return false;
    const cap = offerMaxCartCents(cfg);
    if (cap == null) return true;
    return merchCents < cap;
  }

  /** Round fee up to nearest $0.50 (50 cents). */
  function roundFeeUpToHalfDollar(cents) {
    if (cents <= 0) return 0;
    return Math.ceil(cents / 50) * 50;
  }

  function targetFeeCents(merchCents, cfg) {
    const pct = Number(cfg.percent) || 0;
    const scaled = Math.round(merchCents * (pct * 100)) / 10000;
    let t = Math.round(scaled);
    const minC = cfg.minCents != null && cfg.minCents !== '' ? Number(cfg.minCents) : null;
    if (minC != null && !isNaN(minC) && minC > 0) t = Math.max(t, minC);
    return roundFeeUpToHalfDollar(t);
  }

  function displayCartTotalCents(cart, cfg, productId, optedOut) {
    if (!cart || cfg.pricingMode !== 'cart_transform' || optedOut) {
      return cart?.total_price ?? 0;
    }
    const protectionLines = protectionLineItems(cart, productId);
    if (protectionLines.length !== 1) {
      return cart.total_price;
    }
    const merch = merchandiseSubtotalCents(cart, productId);
    const feeCents = targetFeeCents(merch, cfg);
    const placeholderCents =
      protectionLines[0].final_line_price || protectionLines[0].line_price || 0;
    return cart.total_price - placeholderCents + feeCents;
  }

  function pickVariantId(cfg, targetCents) {
    const variants = Array.isArray(cfg.variants) ? [...cfg.variants] : [];
    if (!variants.length) return null;
    variants.sort((a, b) => (a.price || 0) - (b.price || 0));
    const mode = cfg.tierMode === 'ceil' ? 'ceil' : 'closest';
    if (mode === 'ceil') {
      const found = variants.find((v) => (v.price || 0) >= targetCents);
      return (found || variants[variants.length - 1]).id;
    }
    let best = variants[0];
    let bestDiff = Math.abs((best.price || 0) - targetCents);
    for (let i = 1; i < variants.length; i++) {
      const v = variants[i];
      const d = Math.abs((v.price || 0) - targetCents);
      if (d < bestDiff) {
        best = v;
        bestDiff = d;
      }
    }
    return best.id;
  }

  let syncQueued = false;
  let syncRunning = false;

  async function postCartUpdate(updates) {
    if (!updates || Object.keys(updates).length === 0) return null;
    const sections = getBundledSectionIds();
    const payload = { updates };
    if (sections.length) {
      payload.sections = sections.join(',');
    }
    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'package-protection', cart: data } })
    );
    return data;
  }

  async function postCartJson(body) {
    const sections = getBundledSectionIds();
    const payload = { ...body };
    if (sections.length) {
      payload.sections = sections.join(',');
    }
    const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'package-protection', cart: data } })
    );
    return data;
  }

  function protectionLineProperties(feeCents, cfg) {
    return {
      _pp_percent: String(cfg.percent ?? '2.5'),
      _pp_fee_cents: String(feeCents),
    };
  }

  async function postCartAdd(items) {
    const sections = getBundledSectionIds();
    const payload = { items };
    if (sections.length) {
      payload.sections = sections.join(',');
    }
    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }
    const cart = await fetchCartJson();
    if (data.sections) {
      cart.sections = data.sections;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'package-protection', cart } })
    );
    return cart;
  }

  async function fetchCartJson() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`);
    return res.json();
  }

  async function clearLegacyProtectionCartAttributes(cart) {
    const working = cart || (await fetchCartJson());
    const attrs = working.attributes || {};
    const hasLegacy = LEGACY_CART_ATTRS.some((key) => attrs[key] != null && attrs[key] !== '');
    if (!hasLegacy) {
      return working;
    }

    const cleared = {};
    for (const key of LEGACY_CART_ATTRS) {
      cleared[key] = '';
    }

    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: cleared }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return working;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'package-protection', cart: data } })
    );
    return data;
  }

  async function syncProtection(cart) {
    const cfg = getConfig();
    if (!cfg?.enabled || !cfg.productId) return;

    let working = cart || (await fetchCartJson());
    working = (await clearLegacyProtectionCartAttributes(working)) || working;
    const productId = cfg.productId;

    const refresh = async () => {
      working = await fetchCartJson();
      return working;
    };

    let merch = merchandiseSubtotalCents(working, productId);
    const optedOut = getOptOut();
    let lines = protectionLineItems(working, productId);

    if (optedOut || merch <= 0 || !isOfferEligible(merch, cfg)) {
      if (lines.length) {
        const updates = {};
        for (const line of lines) {
          updates[line.key] = 0;
        }
        await postCartUpdate(updates);
      }
      return;
    }

    const isTransform = cfg.pricingMode === 'cart_transform';
    const target = targetFeeCents(merch, cfg);
    const desiredVariantId = isTransform
      ? cfg.placeholderVariantId
      : pickVariantId(cfg, target);
    if (!desiredVariantId) return;

    let linesNow = protectionLineItems(working, productId);
    const okLine = linesNow.find(
      (l) => String(l.variant_id) === String(desiredVariantId) && l.quantity === 1
    );
    if (linesNow.length === 1 && okLine) {
      const props = protectionLineProperties(target, cfg);
      const existingFee = parseInt(String(okLine.properties?._pp_fee_cents ?? ''), 10);
      const existingPct = String(okLine.properties?._pp_percent ?? '');
      if (existingFee === target && existingPct === props._pp_percent) {
        return;
      }
      await postCartJson({
        id: okLine.key,
        quantity: 1,
        properties: props,
      });
      return;
    }

    if (linesNow.length) {
      const updates = {};
      for (const line of linesNow) {
        updates[line.key] = 0;
      }
      await postCartUpdate(updates);
      await refresh();
    }

    await postCartAdd([
      {
        id: desiredVariantId,
        quantity: 1,
        properties: protectionLineProperties(target, cfg),
      },
    ]);
  }

  async function runSync(cart) {
    if (syncRunning) {
      syncQueued = true;
      return;
    }
    syncRunning = true;
    try {
      await syncProtection(cart);
    } catch (e) {
      console.warn('[package-protection]', e);
    } finally {
      syncRunning = false;
      if (syncQueued) {
        syncQueued = false;
        setTimeout(() => runSync(null), 0);
      }
    }
  }

  let debounceTimer;
  function scheduleSync(cart) {
    if (!getConfig()?.enabled) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSync(cart), 120);
  }

  function setOptOutUi(root, optedOut) {
    if (!root) return;
    root.classList.toggle('package-protection--opted-out', optedOut);
  }

  function applyOptOutUiFromStorage() {
    const optedOut = getOptOut();
    document.querySelectorAll('[data-package-protection]').forEach((root) => {
      setOptOutUi(root, optedOut);
    });
  }

  function updateDomFromCart(cart) {
    const cfg = getConfig();
    if (!cfg?.enabled) return;
    const productId = cfg.productId;
    const optedOut = getOptOut();
    const displayTotal = displayCartTotalCents(cart, cfg, productId, optedOut);
    document.querySelectorAll('[data-package-protection-cart-total]').forEach((el) => {
      el.textContent = formatMoney(displayTotal);
    });
    document.querySelectorAll('[data-package-protection]').forEach((root) => {
      const merch = merchandiseSubtotalCents(cart, productId);
      const eligible = isOfferEligible(merch, cfg);
      root.hidden = !eligible;
      if (!eligible) return;
      setOptOutUi(root, optedOut);
      if (optedOut) return;
      const feeEl = root.querySelector('[data-package-protection-row-text]');
      const feeCents = targetFeeCents(merch, cfg);
      if (feeEl) {
        feeEl.textContent = widgetRowLabel(cfg, feeEl.dataset.packageProtectionRowTemplate, feeCents);
      }
      const label = root.querySelector('[data-package-protection-checkout-label]');
      if (label) {
        label.textContent = checkoutButtonLabel(cfg, displayTotal);
      }
    });
  }

  function getCheckoutForm(root) {
    if (!root) return null;
    const formId = root.dataset.cartFormId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }
    return root.closest('form');
  }

  function goToCheckout() {
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(`${root}checkout`);
  }

  function submitCheckoutForm(form) {
    if (!form) {
      goToCheckout();
      return;
    }
    const existing = form.querySelector('button[name="checkout"][type="submit"], input[name="checkout"][type="submit"]');
    if (existing && typeof form.requestSubmit === 'function') {
      form.requestSubmit(existing);
      return;
    }
    const submitter = document.createElement('button');
    submitter.type = 'submit';
    submitter.name = 'checkout';
    submitter.hidden = true;
    submitter.setAttribute('aria-hidden', 'true');
    form.appendChild(submitter);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.submit();
    }
    submitter.remove();
  }

  function getProtectionDialog(trigger) {
    const dialogId = trigger?.getAttribute('aria-controls');
    if (dialogId) {
      const byId = document.getElementById(dialogId);
      if (byId instanceof HTMLDialogElement) {
        return byId;
      }
    }
    const root = trigger?.closest('[data-package-protection]');
    return root?.querySelector('[data-package-protection-dialog]') || null;
  }

  function prepareDialogs() {
    document.querySelectorAll('[data-package-protection-dialog]').forEach((dialog) => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (dialog.open) {
        dialog.close();
      }
      if (dialog.parentElement !== document.body) {
        document.body.appendChild(dialog);
      }
    });
  }

  function bindDelegatedUi() {
    document.addEventListener('click', async (e) => {
      const openBtn = e.target.closest('[data-package-protection-open]');
      if (openBtn) {
        e.preventDefault();
        const dialog = getProtectionDialog(openBtn);
        if (dialog && typeof dialog.showModal === 'function') {
          if (dialog.parentElement !== document.body) {
            document.body.appendChild(dialog);
          }
          dialog.showModal();
        }
        return;
      }
      const closeBtn = e.target.closest('[data-package-protection-close]');
      if (closeBtn) {
        const dialog = closeBtn.closest('dialog');
        dialog?.close?.();
        return;
      }
      const optOut = e.target.closest('[data-package-protection-opt-out]');
      if (optOut) {
        e.preventDefault();
        if (optOut.disabled) return;
        const root = optOut.closest('[data-package-protection]');
        const checkoutForm = getCheckoutForm(root);
        optOut.disabled = true;
        setOptOut(true);
        setOptOutUi(root, true);
        try {
          await runSync(null);
          if (checkoutForm && document.body.contains(checkoutForm)) {
            submitCheckoutForm(checkoutForm);
          } else {
            goToCheckout();
          }
        } catch (err) {
          console.warn('[package-protection]', err);
          optOut.disabled = false;
        }
        return;
      }
    });

    document.addEventListener('click', (e) => {
      const img = e.target.closest('.package-protection__dialog-img');
      if (img) {
        img.closest('dialog')?.close?.();
        return;
      }
      const dlg = e.target.closest('[data-package-protection-dialog]');
      if (dlg && e.target === dlg) {
        dlg.close();
      }
    });
  }

  function onCartChange(e) {
    const cart = e.detail?.cart;
    prepareDialogs();
    if (!cart) return;
    updateDomFromCart(cart);
    if (e.detail?.baseEvent === 'package-protection') {
      return;
    }
    scheduleSync(cart);
  }

  function onCartRefresh() {
    scheduleSync(null);
  }

  window.themePackageProtection = {
    syncNow() {
      return runSync(null);
    },
    getOptOut,
    setOptOut(value) {
      setOptOut(!!value);
      applyOptOutUiFromStorage();
      return runSync(null);
    },
    getConfig: () => {
      const c = getConfig();
      return c && c.enabled ? { ...c, variants: c.variants ? [...c.variants] : [] } : null;
    },
  };

  if (!getConfig()?.enabled) {
    return;
  }

  bindDelegatedUi();
  document.addEventListener('cart:change', onCartChange);
  document.addEventListener('cart:refresh', onCartRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      prepareDialogs();
      applyOptOutUiFromStorage();
      fetchCartJson().then((cart) => {
        updateDomFromCart(cart);
        scheduleSync(cart);
      });
    });
  } else {
    prepareDialogs();
    applyOptOutUiFromStorage();
    fetchCartJson().then((cart) => {
      updateDomFromCart(cart);
      scheduleSync(cart);
    });
  }
})();
